import { AccountInfo, AuthenticationResult, PublicClientApplication, RedirectRequest } from "@azure/msal-browser";
import { Store } from "../bootstrap/ReduxStore";
import { CHANGE_PASSWORD_URL, FORGOT_PASSWORD_URL, REDIRECT_URI, CLIENT_ID_B2C } from "../config/env";
import { userLoginSuccess, userLoginFailed } from "../modules/auth/redux";
import { loadAction, loadedAction } from "../modules/auth/redux/ActionAuth";
import { hideSessionExpiredModal } from "../modules/session/redux";
import { AuthConfig } from "./AuthConfig";
import { HttpService } from "./HttpService";
import { persistRefreshedToken } from "./tokenRefresh";

export const USER_TYPE = {
  INTERNAL: 'INTERNAL',
  EXTERNAL: 'EXTERNAL'
}

class AuthService {
  pcaClient: PublicClientApplication;
  b2cClient: PublicClientApplication;
  loginRequestInternal: RedirectRequest;
  loginRequestExternal: RedirectRequest;
  account: AccountInfo | null;

  constructor() {
      let config = new AuthConfig()
      this.pcaClient = new PublicClientApplication(config.pcaConfig);
      this.b2cClient = new PublicClientApplication(config.b2cConfig);

      this.loginRequestInternal = {
          scopes: ["openid", "profile"],
          redirectUri: REDIRECT_URI,
      };
      this.loginRequestExternal = {
          scopes: ["openid", "profile", CLIENT_ID_B2C],
          redirectUri: REDIRECT_URI,
      };
      this.account = null;
  }

  loadAuthModule(): void {
    this.pcaClient
      .handleRedirectPromise()
      .then((resp: AuthenticationResult | null) => {
          if (resp) {
            this.handleResponsePCA(resp);
          }else{
            Store.dispatch(loadedAction())
          }
      })
      .catch(err => {
        Store.dispatch(loadedAction())
      });

    this.b2cClient
      .handleRedirectPromise()
      .then((resp: AuthenticationResult | null) => {
          if (resp) {
            this.handleResponseB2C(resp);
          }else{
            Store.dispatch(loadedAction())
          }
      })
      .catch(async err => {
        if(err.message.includes('AADB2C90118')){
          await this.forgotPassword()
        }else{
          Store.dispatch(loadedAction())
        }
      });
  }

  async handleResponsePCA(response: AuthenticationResult) {

    if (response !== null) {
      try {
        localStorage.setItem('userType', USER_TYPE.INTERNAL)
        let userinfo = await this.getUserInfo(response)
        this.assertValidUserInfo(userinfo)
        let finalToken = {
          ...response,
          userinfo: userinfo.data.message.user,
          token: response.idToken,
          username: response.account?.username,
          roles: userinfo.data.message.roles,
          locations: userinfo.data.message.locations,
          exp: response.expiresOn?.getTime()
        }

        persistRefreshedToken(finalToken.token, finalToken.exp)
        Store.dispatch(userLoginSuccess(finalToken))
        Store.dispatch(hideSessionExpiredModal())
        Store.dispatch(loadedAction())
      } catch (err) {
        // A successful MSAL login followed by a failed/malformed userinfo call
        // must still flip isLoading off — otherwise DefaultLayout (and any route
        // that reads state.loader.isLoading) is stuck on "Loading..." forever
        // with no way for the user to recover short of a hard refresh. It must
        // also NOT dispatch userLoginSuccess with incomplete data, since NewLogin
        // then tries to redirect using a `redirect` field that was never set,
        // landing the user on the broken "/#/undefined" route with no way out.
        console.error('handleResponsePCA: getUserInfo failed', err)
        localStorage.removeItem('userType')
        localStorage.removeItem('userinfo')
        Store.dispatch(userLoginFailed({ source: 'USERINFO', message: (err as any)?.message || 'Failed to load user info' }))
        Store.dispatch(loadedAction())
      }
    }else{
      console.log('no response')
    }
  }

  async handleResponseB2C(response: AuthenticationResult) {

    if (response !== null &&
      !response.authority.toLowerCase().includes(CHANGE_PASSWORD_URL.toLowerCase()) &&
      !response.authority.toLowerCase().includes(FORGOT_PASSWORD_URL.toLowerCase())
    ) {
      try {
        let temp: any = response.idTokenClaims;
        let accountTemp: any = {...response.account, username: temp.email}
        localStorage.setItem('userType', USER_TYPE.EXTERNAL)
        let userinfo = await this.getUserInfo({...response, account: accountTemp})
        this.assertValidUserInfo(userinfo)
        let finalToken = {
          ...response,
          userinfo: userinfo.data.message.user,
          token: response.idToken,
          username: temp.email,
          roles: userinfo.data.message.roles,
          locations: userinfo.data.message.locations,
          exp: response.expiresOn?.getTime()
        }

        persistRefreshedToken(finalToken.token, finalToken.exp)
        Store.dispatch(userLoginSuccess(finalToken))
        Store.dispatch(hideSessionExpiredModal())
        Store.dispatch(loadedAction())
      } catch (err) {
        // Same reasoning as handleResponsePCA: don't leave isLoading stuck
        // true forever when a successful B2C login is followed by a failed
        // or malformed userinfo call, and don't dispatch a "success" that
        // sends NewLogin down the broken "/#/undefined" redirect.
        console.error('handleResponseB2C: getUserInfo failed', err)
        localStorage.removeItem('userType')
        localStorage.removeItem('userinfo')
        Store.dispatch(userLoginFailed({ source: 'USERINFO', message: (err as any)?.message || 'Failed to load user info' }))
        Store.dispatch(loadedAction())
      }
    }else{
      console.log('no response')
      if(response.authority.toLowerCase().includes(FORGOT_PASSWORD_URL.toLocaleLowerCase())){
        Store.dispatch(loadedAction())
        localStorage.clear()
      }else if(response.authority.toLowerCase().includes(CHANGE_PASSWORD_URL.toLocaleLowerCase())){
        Store.dispatch(loadAction())
        this.signOut()
      }
    }
  }

  // Returns loginRedirect's promise rather than swallowing it: the login page
  // keeps its blocking spinner up until the browser actually navigates away,
  // so a redirect that fails outright must surface to the caller's catch
  // instead of leaving that spinner hanging forever.
  signInInternal(email: string): Promise<void> {
    return this.pcaClient.loginRedirect({...this.loginRequestInternal, loginHint: email});
  }

  signInB2C(email: string): Promise<void> {
    return this.b2cClient.loginRedirect({...this.loginRequestExternal, loginHint: email});
  }

  signUp(): void{
    let config = new AuthConfig()
    let custConfig = new PublicClientApplication(config.signupConfig)
    custConfig.loginRedirect();
  }

  async signOut(): Promise<void> {
    let userType = localStorage.getItem('userType');
    if(userType === USER_TYPE.INTERNAL){
      localStorage.clear();
      await this.pcaClient.logout({account: this.getAccounts()[0]});
    }else{
      localStorage.clear();
      await this.b2cClient.logout();
    }
  }

  onSessionExpired(): void {
    // Deliberately NOT clearing localStorage here (unlike signOut()). This
    // tab's session is dead and it's navigating away regardless, but a
    // `localStorage.clear()` fires a `storage` event that sibling tabs'
    // AuthGate reads as "logged out" (see AuthGate.tsx's storage handler)
    // and forces them to login too — even if they're still fine, or about
    // to be rescued once this tab re-authenticates and writes a fresh
    // token back. Leaving the stale state in place is harmless: it just
    // gets overwritten the next time this tab (or a sibling) logs in.
    window.location.href = '/#/login';
  }

  async getTokenSilent(): Promise<AuthenticationResult | void>{
    let userType = localStorage.getItem('userType')
    if(userType === USER_TYPE.INTERNAL){
      let accounts = this.pcaClient.getAllAccounts()
      try{
        // forceRefresh: true — this method is only ever called from the
        // token refresh path, i.e. we already know the stored token is
        // expired or about to expire. Without this, MSAL can decide its
        // own cached entry is "still valid" (its renewal heuristic is
        // driven by the access-token cache entry, not by the idToken's
        // own `exp` claim — and loginRequestInternal only asks for
        // openid/profile, no resource scope) and hand back the same
        // stale idToken with no network round-trip, which the backend
        // then rejects again as expired.
        return await this.pcaClient.acquireTokenSilent({...this.loginRequestInternal, account: accounts[0], forceRefresh: true})
      }catch(err){
        // This tab's own MSAL cache/refresh token has an issue, but the
        // underlying Azure AD session (SSO cookie at login.microsoftonline.com)
        // may still be alive — ssoSilent re-authenticates against that shared
        // session via a hidden iframe instead of this app's own token cache,
        // so it can recover even when acquireTokenSilent can't.
        try{
          return await this.pcaClient.ssoSilent({...this.loginRequestInternal, loginHint: accounts[0]?.username})
        }catch{
          return undefined
        }
      }
    }else if(userType === USER_TYPE.EXTERNAL){
      let accounts = this.b2cClient.getAllAccounts()
      try{
        // See the internal-account branch above for why forceRefresh is needed.
        return await this.b2cClient.acquireTokenSilent({...this.loginRequestExternal, account: accounts[0], forceRefresh: true})
      }catch(err){
        try{
          return await this.b2cClient.ssoSilent({...this.loginRequestExternal, loginHint: accounts[0]?.username})
        }catch{
          return undefined
        }
      }
    }else{
      return undefined
    }
  }

  // getUserInfo can resolve with HTTP 200 but a body that doesn't have the
  // shape the login flow relies on (e.g. `Roles: null` from an upstream
  // outage) — that isn't a thrown/rejected request, so it would otherwise
  // sail straight into userLoginSuccess with holes in it. Fail loudly here
  // instead of downstream where the missing fields are harder to trace back
  // to "the userinfo call didn't actually return usable data".
  assertValidUserInfo(userinfo: any): void {
    const message = userinfo?.data?.message;
    if (!message || !message.user) {
      throw new Error('userinfo response is missing user data');
    }
  }

  async getUserInfo(authResult: AuthenticationResult){
    const headers = {
      'Token-Auth': authResult.idToken,
    }
    let body = {
      Action: 'READ_USER',
      User: {
          Email: authResult.account?.username,
      },
      ApplicationCode: 'Lintas',
    };

    // skipTokenRefresh: the `Token-Auth` header above is the token MSAL just
    // handed us, which is by definition fresher than anything in
    // localStorage['userinfo'] (that key is only rewritten by
    // persistRefreshedToken *after* this call returns). Without the flag,
    // HttpService would judge the stale stored token expired, kick off an
    // acquireTokenSilent right while handleRedirectPromise is still settling,
    // and — when that fails — fire 'token-expired', popping the "Session
    // Expired" modal on top of the post-login loading screen.
    let result: any = await HttpService.post('authentication/userinfo', body, headers, { skipTokenRefresh: true });

    return result;
  }

  async getUserCompanyInfo(userInfo:any, authResult: AuthenticationResult){
    const headers = {
      'Token-Auth': authResult.idToken,

    }
    let body = {
      action: 'READ_USER_COMPANY_LIST',
      User: {
        ID: userInfo.ID,
      },
    };

    // Deliberately NOT skipTokenRefresh (unlike getUserInfo): this one is
    // called from the dashboard long after login, and its `authResult` is a
    // stub built from redux state rather than a live MSAL result — so the
    // `Token-Auth` above is not trustworthy and HttpService's own refresh is
    // what actually supplies a valid token here.
    let result: any = await HttpService.post('authentication/users', body, headers);

    return result;
  }

  getAccounts(): AccountInfo[]{
    let userType = localStorage.getItem('userType')
      if(userType === USER_TYPE.INTERNAL){
        return this.pcaClient.getAllAccounts()
      }else{
        return this.b2cClient.getAllAccounts()
      }
  }

  async forgotPassword(){
    let config = new AuthConfig();
    let forgotPassword = new PublicClientApplication(config.forgotPasswordConfig);
    forgotPassword.loginRedirect();
  }

  async isUserInternal(email: string){
    // Pre-login lookup: there is no session to refresh, and the stale
    // userType/userinfo that onSessionExpired() deliberately leaves behind
    // would otherwise send this through attemptTokenRefresh() and fire a
    // "Session Expired" modal on the login page itself.
    let result = await HttpService.get(`authentication/ad/users/${email}`, null, {}, { skipTokenRefresh: true });
    if(result.status === 200 && result.data.message.length > 0){
      return true;
    }else{
      return false;
    }
  }

  async getExternalInfo(email: string){
    // Pre-login lookup — see isUserInternal above.
    let result = await HttpService.get(`authentication/external/${email}`, null, {}, { skipTokenRefresh: true });
    if(result.status === 200){
      return result.data.message;
    }else{
      return null;
    }
  }

  changePassword(){
    let config = new AuthConfig();
    let changePassword = new PublicClientApplication(config.changePasswordConfig);
    changePassword.loginRedirect();
  }
}

const authService = new AuthService();

export default authService;
